import { createRootRoute, Outlet } from '@tanstack/react-router';
import Navbar from '@/shared/components/layout/Navbar';
import Footer from '@/shared/components/layout/Footer';
import { useAuth } from '@/features/auth/providers/AuthProvider';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { PwaUpdateModal } from '@/shared/components/feedback/PwaUpdateModal';
import { OfflineOverlay } from '@/shared/components/feedback/OfflineOverlay';
import { OnboardingProvider } from '@/features/onboarding/providers/OnboardingProvider';
import { AppBanners } from '@/shared/components/AppBanners';
import { NotFound } from '@/shared/components/feedback/NotFound';
import { RouteSkeletonLoader } from '@/shared/components/feedback/skeletons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Toaster } from 'sonner';
import { useTheme } from '@/shared/providers/ThemeProvider';
import { SidebarProvider, useSidebar } from '@/shared/providers/SidebarProvider';
import { SidebarContent } from '@/shared/components/layout/AppSidebar';
import { Sheet, SheetContent, SheetTitle } from '@/shared/components/ui/sheet';
import { cn } from '@/shared/utils/utils';

const publicRoutes = ['/login', '/logout', '/'];
/** Full-screen routes that draw their own chrome. */
const storyRoutes = ['/wrap'];
/** Matches Tailwind's `lg` breakpoint. */
const DESKTOP_SIDEBAR_QUERY = '(min-width: 1024px)';

function useDesktopSidebar() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(DESKTOP_SIDEBAR_QUERY).matches : false
  );

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_SIDEBAR_QUERY);
    const sync = () => setIsDesktop(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return isDesktop;
}

function DesktopSidebar({ skeleton }: { skeleton?: boolean }) {
  const [hovered, setHovered] = useState(false);
  const [locked, setLocked] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>(null);
  const asideRef = useRef<HTMLElement>(null);
  const visible = hovered || locked;

  const handleEnter = useCallback(() => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    setHovered(true);
  }, []);

  const handleLeave = useCallback(() => {
    leaveTimer.current = setTimeout(() => setHovered(false), 500);
  }, []);

  const handleUnlock = useCallback(() => {
    setLocked(false);
    // When a portal (dropdown / popover) closes, the browser may never have
    // fired mouseLeave on the <aside> because the pointer went straight to
    // the portal overlay. Re-check whether the pointer is actually inside
    // the sidebar; if not, start the leave timer so labels fade out.
    requestAnimationFrame(() => {
      const el = asideRef.current;
      if (!el) return;
      if (!el.matches(':hover')) {
        setHovered(false);
      }
    });
  }, []);

  useEffect(() => {
    return () => {
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    };
  }, []);

  return (
    <aside
      ref={asideRef}
      className="flex shrink-0 w-14 relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <div
        className={cn(
          'absolute inset-y-0 -left-48 right-0 pointer-events-none transition-opacity duration-150',
          visible ? 'opacity-80' : 'opacity-0'
        )}
        style={{
          background: 'linear-gradient(to right, transparent, var(--background) 50%)',
        }}
      />
      <div className="relative z-10">
        <SidebarContent
          skeleton={skeleton}
          hovered={visible}
          onLockHover={() => setLocked(true)}
          onUnlockHover={handleUnlock}
        />
      </div>
    </aside>
  );
}

function AppBootShell({ isDesktopSidebar }: { isDesktopSidebar: boolean }) {
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex flex-1">
        <div className="flex-1 flex flex-col min-w-0">
          <Navbar skeleton />
          <main className="flex-1 flex flex-col">
            <RouteSkeletonLoader />
          </main>
        </div>
        {isDesktopSidebar && <DesktopSidebar skeleton />}
      </div>
      <Footer />
    </div>
  );
}

function RootLayout() {
  const { user, initialized, roleReady } = useAuth();
  const { resolvedTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const isPublicRoute = publicRoutes.includes(location.pathname);
  const isStoryRoute = storyRoutes.includes(location.pathname);
  const isDesktopSidebar = useDesktopSidebar();

  useEffect(() => {
    if (isPublicRoute) {
      document.documentElement.classList.remove('dark');
      return () => {
        if (resolvedTheme === 'dark') {
          document.documentElement.classList.add('dark');
        }
      };
    }
  }, [isPublicRoute, resolvedTheme]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_NAVIGATE' && event.data.url) {
        window.location.href = event.data.url;
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handler);
    };
  }, []);

  const isAdminRoute = location.pathname.startsWith('/admin');

  if (!initialized && !isPublicRoute) {
    if (isStoryRoute) return <div className="fixed inset-0 bg-wrap-night" />;
    return <AppBootShell isDesktopSidebar={isDesktopSidebar} />;
  }

  if (!user && !isPublicRoute) {
    navigate({
      to: '/login',
      search: { from: location.pathname },
      replace: true,
    });
    return null;
  }

  // The first signed-in user object uses a placeholder role. Hold the admin
  // route until the real role arrives, then send regular users home before
  // the admin page can render.
  if (user && isAdminRoute && !roleReady) {
    return <AppBootShell isDesktopSidebar={isDesktopSidebar} />;
  }

  if (user && isAdminRoute && user.role === 'user') {
    navigate({ to: '/', replace: true });
    return null;
  }

  if (isStoryRoute) {
    return (
      <OnboardingProvider enabled={false}>
        <OfflineOverlay />
        <PwaUpdateModal />
        <Toaster theme={resolvedTheme} />
        <Outlet />
      </OnboardingProvider>
    );
  }

  const showSidebar = !!user && !isPublicRoute;

  return (
    <OnboardingProvider enabled={!!user && !isPublicRoute}>
      <OfflineOverlay />
      <PwaUpdateModal />
      <Toaster theme={isPublicRoute ? 'light' : resolvedTheme} />
      <div className="min-h-screen flex flex-col">
        {/* Above the header row. The desktop name and email float out of the
            sidebar, so a banner inside that row slides underneath them. */}
        {user && !isPublicRoute && <AppBanners />}
        <div className="flex flex-1">
          <div className="flex-1 flex flex-col min-w-0">
            {user && <Navbar />}
            <main className="flex-1 flex flex-col">
              <Outlet />
            </main>
          </div>

          {showSidebar && isDesktopSidebar && <DesktopSidebar />}
          {showSidebar && !isDesktopSidebar && <MobileSidebarSheet />}
        </div>

        {(user || location.pathname === '/' || location.pathname === '/login') && <Footer />}
      </div>
    </OnboardingProvider>
  );
}

function MobileSidebarSheet() {
  const { open, setOpen } = useSidebar();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-60 p-0" showCloseButton={false}>
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <SidebarContent mobile onNavigate={() => setOpen(false)} />
      </SheetContent>
    </Sheet>
  );
}

export const Route = createRootRoute({
  notFoundComponent: NotFound,
  component: () => (
    <SidebarProvider>
      <RootLayout />
    </SidebarProvider>
  ),
});
